import { motion } from "framer-motion";
import { Link as ScrollLink } from "react-scroll";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGithub, faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { faEnvelope, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useBooking } from "../context/BookingContext";

const socialLinks = [
  { icon: faGithub,   href: "https://github.com/AkashZaheer045",                    label: "GitHub"   },
  { icon: faLinkedin, href: "https://www.linkedin.com/in/muhammadakashzaheer/",      label: "LinkedIn" },
  { icon: faEnvelope, href: "mailto:akashzaheer045@gmail.com",                       label: "Email"    },
];

const codeLines = [
  { tokens: [{ t: "const", c: "kw" }, { t: " developer", c: "va" }, { t: " = {", c: "ba" }] },
  { tokens: [{ t: "  name",       c: "pr" }, { t: ": ", c: "ba" }, { t: '"Muhammad Akash Zaheer"',  c: "st" }, { t: ",", c: "ba" }] },
  { tokens: [{ t: "  role",       c: "pr" }, { t: ": ", c: "ba" }, { t: '"Full-Stack Engineer"',    c: "st" }, { t: ",", c: "ba" }] },
  { tokens: [{ t: "  experience", c: "pr" }, { t: ": ", c: "ba" }, { t: '"3+ years"',               c: "st" }, { t: ",", c: "ba" }] },
  { tokens: [{ t: "  stack",      c: "pr" }, { t: ": [", c: "ba" }, { t: '"Next.js"', c: "st" }, { t: ", ", c: "ba" }, { t: '"Node.js"', c: "st" }, { t: ", ", c: "ba" }, { t: '"TypeScript"', c: "st" }, { t: "],", c: "ba" }] },
  { tokens: [{ t: "  cloud",      c: "pr" }, { t: ": [", c: "ba" }, { t: '"AWS"', c: "st" }, { t: ", ", c: "ba" }, { t: '"Azure"', c: "st" }, { t: ", ", c: "ba" }, { t: '"Docker"', c: "st" }, { t: "],", c: "ba" }] },
  { tokens: [{ t: "  focus",      c: "pr" }, { t: ": [", c: "ba" }] },
  { tokens: [{ t: '    "Microservices"',    c: "st" }, { t: ",",  c: "ba" }, { t: "   // 12-module platform", c: "cm" }] },
  { tokens: [{ t: '    "Multi-Tenant SaaS"', c: "st" }, { t: ",", c: "ba" }, { t: " // row-level isolation",  c: "cm" }] },
  { tokens: [{ t: '    "AI Integration"',   c: "st" }, { t: ",",  c: "ba" }, { t: "   // LLM APIs, Cursor",   c: "cm" }] },
  { tokens: [{ t: "  ],", c: "ba" }] },
  { tokens: [{ t: "  delivery",   c: "pr" }, { t: ": ", c: "ba" }, { t: '"concept → deploy"', c: "st" }, { t: ",", c: "ba" }] },
  { tokens: [{ t: "}", c: "ba" }] },
];

/* Token → className. Light/dark rules live in index.css to avoid duplication. */
const tk = { kw: "ck", va: "cv", pr: "cp", st: "cs", cm: "cc", ba: "cb" };

const Home = () => {
  const { openBooking } = useBooking();

  return (
    <section
      id="mainy"
      className="responsive-hero relative min-h-screen flex flex-col justify-center overflow-hidden
                 px-3 min-[360px]:px-4 sm:px-6 md:px-10 lg:px-12 pt-32 sm:pt-36 pb-12"
    >
    <div className="ambient-glow-1 animate-pulse-slow" aria-hidden="true" />
    <div className="ambient-glow-2"                   aria-hidden="true" />

    <div className="w-full max-w-7xl mx-auto relative z-10 overflow-hidden lg:overflow-visible">
      {/* ── 60/40 grid ── */}
      <div className="grid min-w-0 lg:grid-cols-[3fr_2fr] gap-10 sm:gap-12 lg:gap-16 items-center">

        {/* ── Left: copy ── */}
        <div className="responsive-hero__panel flex flex-col items-center lg:items-start text-center lg:text-left mx-auto lg:mx-0">

          {/* Availability badge */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="inline-flex max-w-full items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full
                       bg-background/80 backdrop-blur-md border border-primary/20 mb-7 sm:mb-8 shadow-soft"
          >
            <span className="relative flex h-2 w-2 flex-shrink-0" aria-hidden="true">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            <span className="text-[11px] sm:text-xs font-bold text-foreground tracking-wide whitespace-nowrap">
              Available for New Projects
            </span>
          </motion.div>

          {/* H1 — punchy 7-word headline */}
          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="w-full max-w-xl text-[2rem] min-[360px]:text-[2.35rem] sm:text-[2.75rem]
                       md:text-[3.1rem] lg:text-[3.4rem] font-heading font-extrabold mb-4
                       tracking-tight text-foreground leading-[1.08]"
            style={{ textWrap: "balance" }}
          >
            Architecting Scalable Apps &amp;{" "}
            <span className="text-primary">Custom AI Automations.</span>
          </motion.h1>

          {/* Role line */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.17 }}
            className="text-base sm:text-lg text-muted-foreground font-body font-medium
                       mb-8 sm:mb-10 leading-relaxed max-w-md"
          >
            Full-Stack Engineer &amp; AI Systems Consultant — shipping{" "}
            <span className="text-foreground font-bold">production-ready products</span>{" "}
            from first commit to live deployment.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.26 }}
            className="flex flex-col sm:flex-row items-stretch sm:items-center
                       gap-3 sm:gap-4 mb-8 sm:mb-9 w-full sm:w-auto"
          >
            <button
              onClick={openBooking}
              className="w-full sm:w-auto btn-primary"
              id="hero-cta-strategy"
            >
              Book a Strategy Call
              <FontAwesomeIcon icon={faArrowRight} className="w-3.5 h-3.5" />
            </button>

            <ScrollLink to="case-studies" smooth duration={600} offset={-80} className="w-full sm:w-auto">
              <button
                id="hero-cta-work"
                className="w-full sm:w-auto px-6 sm:px-8 py-3.5 bg-background/80 backdrop-blur-md
                           text-foreground border border-border rounded-full font-bold
                           hover:bg-secondary hover:border-primary/30 transition-all duration-300
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                           shadow-soft hover:-translate-y-0.5"
              >
                View Selected Work
              </button>
            </ScrollLink>
          </motion.div>

          {/* Social icons */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.38 }}
            className="flex items-center gap-0.5"
          >
            {socialLinks.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target={s.href.startsWith("http") ? "_blank" : undefined}
                rel={s.href.startsWith("http") ? "noreferrer" : undefined}
                aria-label={s.label}
                className="p-2.5 text-muted-foreground hover:text-primary hover:bg-primary/10
                           transition-all duration-300 rounded-full
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <FontAwesomeIcon icon={s.icon} className="w-[18px] h-[18px]" />
              </a>
            ))}
          </motion.div>
        </div>

        {/* ── Right: terminal card (desktop only) ── */}
        <motion.div
          initial={{ opacity: 0, x: 28, scale: 0.97 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 0.65, delay: 0.28, type: "spring", stiffness: 55 }}
          className="responsive-hero__panel max-w-md mx-auto lg:mx-0 lg:ml-auto
                     relative hidden lg:flex flex-col gap-3"
          aria-hidden="true"
        >
          {/* glow halo */}
          <div className="absolute inset-0 bg-primary/10 blur-3xl rounded-[3rem]
                          opacity-60 -z-10 animate-pulse-slow" />

          {/* editor card */}
          <div className="editor-surface rounded-2xl overflow-hidden shadow-elevated">
            {/* chrome bar */}
            <div className="editor-chrome flex items-center px-4 py-2.5 gap-3">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400/80"   />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-400/80"  />
              </div>
              <span className="flex-1 text-center text-[11px] font-mono text-muted-foreground
                               tracking-wide opacity-60 pr-10">
                developer.ts
              </span>
            </div>

            {/* code */}
            <div className="px-4 py-4 font-mono text-[12.5px] leading-[1.75] overflow-x-auto">
              {codeLines.map((line, i) => (
                <div key={i} className="flex">
                  <span className="editor-ln flex-shrink-0">{i + 1}</span>
                  <span>
                    {line.tokens.map((tok, j) => (
                      <span key={j} className={tk[tok.c]}>{tok.t}</span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* stat pills */}
          <div className="flex items-center gap-2 flex-wrap px-1">
            {["3+ Years Experience", "12-Module Platform", "AWS · Azure · DO"].map((label) => (
              <span key={label} className="stat-pill">
                <span className="stat-pill-dot" aria-hidden="true" />
                {label}
              </span>
            ))}
          </div>
        </motion.div>

      </div>
    </div>
  </section>
  );
};

export default Home;
